import React, { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'

import { OEContext } from '@aytm/app/stats/page/comments'

import { CSS_SUPPORTS_STICKY } from '@aytm/lib/common/func'
import {
  getCommentsCount,
  hasDisabledSentimentInScope,
  questionOldIdToId,
  removeLogicFromText,
  searchToFilterLogic
} from '@aytm/app/stats/page/comments/lib/utils'
import {
  DEFAULT_SELECTED_QUESTION,
  ORDER_BY_OPTIONS,
  PER_PAGE_OPTIONS,
  PER_PAGE_OPTIONS_QA,
  SENTIMENTS_ICON_HASH
} from '@aytm/app/stats/page/comments/lib/constants'
import { changeTagState } from '@aytm/app/stats/page/comments/actions'

import useEventListener from '@aytm/app/stats/hooks/useEventListener'
import downloadAndPackImages from '@aytm/app/stats/page/comments/lib/download/invoke'

//components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { Paginator } from 'primereact/paginator'

import SearchInput from '@aytm/app/stats/page/components/SearchInput'
import SurveyDropdown from '@aytm/app/stats/page/comments/comments/components/SurveyDropdown'

import style from '@aytm/app/stats/page/comments/styles/header.module.scss'

let errorTimer = null

const SurveyHeaderComments = ({
  survey,
  readOnly,
  specialMode,
  questionsList,
  showTraitsRef,
  loadRejectedCS,
  responseOrderRef,
  currentResponseRef,
  isVirtualOrXpertInScope,
  isOnlyVirtualOrXpert,
  isAdvancedFiltersDisabled,
  isSpecialsSortingDisabled,
  onUpdateSchema,
  onCreateFilter,
  onTranslationChange,
  onSelectedQuestionChange,
  onPageChange,
  fetchData,
  fetchDataDelay
}) => {
  const { searchState, translation, dispatch } = useContext(OEContext)
  const {
    request,
    pageData,
    tagsList,
    sentimentOptions,
    qaTotal,
    qaCounter,
    totalPages,
    totalResponses,
    totalComments,
    totalImageResponses,
    totalVideoResponses,
    totalVerbatims,
    totalVerbatimsByLocale,
    translationsMissing,
    isTagsManagementDisabled,
    isSentimentsManagementDisabled,
    isQAMode,
    isQALoading,
    isLoading,
    isProcessing,
    isShowAllTools,
    isShowAllTraits,
    isShowSentiments,
    isSurveyGuest,
    isVideoResponsesMode
  } = searchState
  const { query, selectedQuestion, pageSize, orderBy, orderDirection, currentPage } = request

  const [isDisabledSentimentInScope, isOnlyDisabledSentiment] = hasDisabledSentimentInScope(
    questionsList,
    sentimentOptions,
    selectedQuestion
  )

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const [dndDroppedTag, setDndDroppedTag] = useState(null)

  const [isShowDNDDropzone, setIsShowDNDDropzone] = useState(false)

  const [isShowToolsDropdown, setIsShowToolsDropdown] = useState(false)

  const [downloadProgress, setDownloadProgress] = useState(0)
  const [isDownloading, setIsDownloading] = useState(false)

  const [translationMessage, setTranslationMessage] = useState(null)
  const [translationMessageType, setTranslationMessageType] = useState(null)

  const commentsOnPageCount =
    dndDroppedTag && !isOnlyVirtualOrXpert ? getCommentsCount(pageData, dndDroppedTag, sentimentOptions) : 0
  const totalCommentsSum =
    dndDroppedTag && 'sentiment' != dndDroppedTag.type
      ? totalComments + totalImageResponses + totalVideoResponses
      : totalComments + totalVideoResponses
  const totalCommentsCount =
    !isOnlyVirtualOrXpert && !(dndDroppedTag && 'sentiment' == dndDroppedTag.type && isOnlyDisabledSentiment)
      ? dndDroppedTag && Object.hasOwn(dndDroppedTag, 'count')
        ? totalCommentsSum >= dndDroppedTag.count
          ? totalCommentsSum - dndDroppedTag.count
          : 0
        : totalCommentsSum
      : 0

  const dndTargetCodebookRef = useRef(null)
  const dndTargetTagRef = useRef(null)

  const onDNDDropCodebook = dropData => {
    const codebook = dropData.data

    dispatchEvent(new CustomEvent('runCodingSkipper', { detail: { question: selectedQuestion, codebook } }))
  }

  const onDNDDropTag = dropData => {
    setDndDroppedTag(dropData.data)
  }

  const dndTargetCodebook = ADND.targets.data({ acceptTypes: ['sidebar-codebook'] }).onDrop(onDNDDropCodebook)

  const dndTargetTag = ADND.targets.data({ acceptTypes: ['sidebar-tag', 'sidebar-tag-sentiment'] }).onDrop(onDNDDropTag)

  const headerTop = useMemo(() => {
    const stickyHeader = document.querySelector('.stats-StickyHeader')

    if (stickyHeader) {
      const stickyHeaderRect = stickyHeader.getBoundingClientRect()
      return stickyHeaderRect.height + 20
    } else return 20

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState])

  const isOnePerPage = useMemo(() => 1 == pageSize, [pageSize])
  const isDownloadDisabled = useMemo(
    () => !totalVideoResponses || isDownloading || isLoading || isProcessing,
    [totalVideoResponses, isDownloading, isLoading, isProcessing]
  )

  const question = useMemo(
    () => questionsList.find(q => q.value == selectedQuestion) || null,
    [questionsList, selectedQuestion]
  )

  const questionsListOptions = useMemo(
    () =>
      questionsList
        .filter(item => !item.isOptionHidden)
        .map(item => Object.assign(item, { text: removeLogicFromText(item.text.replace(/<br>/g, ' ')) })),
    [questionsList]
  )

  const hasQuestionOrder = useMemo(() => survey && survey.options && survey.options.sessionOrder, [survey])
  const hasTraits = useMemo(() => Boolean(survey.traits != undefined && 1 < survey.traits.length), [survey.traits])
  const isEmptyScope = useMemo(
    () => !query.length && selectedQuestion == DEFAULT_SELECTED_QUESTION,
    [query, selectedQuestion]
  )

  const orderByOptions = useMemo(
    () =>
      ORDER_BY_OPTIONS.filter(item => {
        let visible = isSpecialsSortingDisabled ? item.isSimple : true

        if ('sentiment' == item.value && !isShowSentiments) {
          visible = false
        }

        if ('similarity' == item.value && !questionsList.some(_item => _item.type == item.forType)) {
          visible = false
        }
        if ('similarity' == item.value && 'on' != window.features.oe_sort_similarity?.status) {
          visible = false
        }

        if (
          question &&
          item.notForType &&
          item.notForType.includes(question.type) &&
          ['sentiment', 'length'].includes(item.value)
        ) {
          visible = false
        }

        return visible
      }).map(item => {
        const newItem = Object.assign({}, item)

        if (newItem.forType && (!question || (question && question.type != newItem.forType))) {
          newItem.disabled = true
        }

        if (newItem.notForType && question && newItem.notForType.includes(question.type)) {
          newItem.disabled = true
        }

        if (orderBy == newItem.value) {
          newItem.text = `${newItem.text} ${'asc' == orderDirection ? '&uarr;' : '&darr;'}`
        }

        return newItem
      }),
    [question, questionsList, orderBy, orderDirection, isShowSentiments, isSpecialsSortingDisabled]
  )

  const translationsOptions = useMemo(() => {
    if (!Object.hasOwn(survey, 'targetingLocales')) return []

    const languageNames = new Intl.DisplayNames(['en'], { type: 'language' })

    const researcherLanguage = survey.locale.split('_', 1)[0].toLowerCase()
    const surveyLanguages = survey.targetingLocales.map(item => item.split('_', 1)[0].toLowerCase())

    const showOriginalLangsLabel =
      'WRD' != survey.country.id && (1 < surveyLanguages.length || !surveyLanguages.some(item => 'en' == item))
    const originalLabel = showOriginalLangsLabel
      ? `Original (${surveyLanguages.join(', ').toUpperCase()})`
      : 'Show original'
    const translations = [{ text: originalLabel, value: '*', icon: 'translate' }]

    if (
      ('en' == researcherLanguage && surveyLanguages.some(item => 'en' != item)) ||
      ('en' != researcherLanguage && (surveyLanguages.some(v => v != researcherLanguage) || 'WRD' != survey.country.id))
    ) {
      translations.push({
        text: `<b class="p-text-icon">EN</b> Show ${languageNames.of('en')} translation`,
        value: 'en',
        label: `<b>EN</b>`,
        translationsMissing: Boolean(translationsMissing?.en)
      })
    }

    if ('en' != researcherLanguage && surveyLanguages.some(v => v != researcherLanguage)) {
      translations.push({
        text: `<b class="p-text-icon">${researcherLanguage.toUpperCase()}</b> Show ${languageNames.of(
          researcherLanguage
        )} translation`,
        value: researcherLanguage,
        label: `<b>${researcherLanguage.toUpperCase()}</b>`,
        translationsMissing: Boolean(translationsMissing?.[researcherLanguage])
      })
    }

    return translations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey.country, survey.locale, survey.targetingLocales, translationsMissing])

  const pageSizeOptions = useMemo(
    () =>
      isQAMode
        ? ['rejected', 'completes_rejected'].includes(loadRejectedCS)
          ? PER_PAGE_OPTIONS_QA.slice(1)
          : PER_PAGE_OPTIONS_QA
        : PER_PAGE_OPTIONS,
    [isQAMode, loadRejectedCS]
  )

  const onRefresh = () => fetchDataDelay(true)

  const onSelectQuestion = event => {
    const selected = event.value
    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: true } })
    dispatch({
      type: 'SET_REQUEST_PARAM_SET',
      request: {
        selectedQuestion: selected,
        currentPage: 1
      }
    })
    if (onSelectedQuestionChange && 'function' == typeof onSelectedQuestionChange) {
      onSelectedQuestionChange(selected)
    }
  }

  const onError = useCallback(error => {
    setError(error.message)

    errorTimer && clearTimeout(errorTimer)
    errorTimer = setTimeout(() => {
      setError(null)
      setDndDroppedTag(null)
    }, 5000)

    return () => errorTimer && clearTimeout(errorTimer)
  }, [])

  const onTagCancel = () => {
    setDndDroppedTag(null)
  }

  const onTagApply = useCallback(
    (isForCurrentPage = false) => {
      const { tag, type, value, label, codebook } = dndDroppedTag

      setLoading(true)

      const _tag = {
        tag: 'sentiment' == type ? value : tag,
        type: type || 'manual',
        label: label,
        codebook: codebook || null
      }

      dispatchEvent(new CustomEvent('codingLoading', { detail: { tag: _tag, isLoading: true } }))

      const data = {
        tag: _tag,
        request,
        action: 'create',
        onFetch: onUpdateSchema,
        onError,
        setIsLoading: flag => {
          if (flag) return

          setDndDroppedTag(null)

          setLoading(false)
          setIsShowDNDDropzone(false)

          dispatchEvent(new CustomEvent('codingLoading', { detail: { isLoading: false } }))
        }
      }

      if (isForCurrentPage) {
        data.responseId = pageData.map(item => item.response.uid)
      }

      changeTagState(data)
    },
    [request, pageData, dndDroppedTag, onError, onUpdateSchema]
  )

  const onToggleToolsDropdown = useCallback(
    (_, val) => {
      setIsShowToolsDropdown(val != undefined ? val : !isShowToolsDropdown)
    },
    [isShowToolsDropdown]
  )

  const onToggleAllTools = useCallback(
    (_, val = null) => {
      const value = null !== val ? val : !isShowAllTools
      dispatch({ type: 'SET_PARAM', request: { name: 'isShowAllTools', value } })
    },
    [dispatch, isShowAllTools]
  )

  const onToggleAllTraits = useCallback(() => {
    dispatch({ type: 'SET_PARAM', request: { name: 'isShowAllTraits', value: !isShowAllTraits } })
    Object.keys(showTraitsRef.current).forEach(key => {
      showTraitsRef.current[key](!isShowAllTraits)
    })
  }, [dispatch, showTraitsRef, isShowAllTraits])

  const onToggleQAMode = useCallback(() => {
    dispatch({ type: 'SET_QA_MODE', request: { value: !isQAMode } })
    setIsShowToolsDropdown(false)
  }, [dispatch, isQAMode])

  const onNewFilter = useCallback(() => {
    if (isEmptyScope) return
    onToggleToolsDropdown(null, false)
    onCreateFilter(searchToFilterLogic(query, selectedQuestion, questionsList, survey))
  }, [survey, query, selectedQuestion, questionsList, isEmptyScope, onCreateFilter, onToggleToolsDropdown])

  const onLoadPerPage = useCallback(
    event => {
      const newPageSize = event.value
      if (pageSize != newPageSize) {
        const newTotalPages = Math.ceil(totalResponses / newPageSize)
        const newCurrentPage = Math.ceil(
          currentResponseRef?.current
            ? (pageSize * (currentPage - 1) + currentResponseRef.current?.index + 1) / newPageSize
            : 1
        )
        dispatch({
          type: 'SET_PARAMS',
          request: { value: { totalPages: newTotalPages, isProcessing: true, isPreserveState: true } }
        })
        dispatch({
          type: 'SET_REQUEST_PARAM_SET',
          request: {
            pageSize: newPageSize,
            currentPage: newCurrentPage
          }
        })
      }
    },
    [dispatch, pageSize, currentPage, totalResponses, currentResponseRef]
  )

  const onSelectOrderBy = useCallback(
    event => {
      const req = {
        currentPage: 1
      }
      if (event.value == orderBy) {
        req.orderDirection = 'asc' == orderDirection ? 'desc' : 'asc'
      } else {
        req.orderBy = event.value
        req.orderDirection = ORDER_BY_OPTIONS.find(option => option.value == event.value).defaultOrder
      }

      dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: true } })
      dispatch({
        type: 'SET_REQUEST_PARAM_SET',
        request: req
      })
    },
    [dispatch, orderBy, orderDirection]
  )

  const onSearchChange = value => {
    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: true } })
    dispatch({
      type: 'SET_REQUEST_PARAM_SET',
      request: {
        query: value.query,
        currentPage: 1
      }
    })
  }

  const onEveryFileCallback = (percent = 0) => {
    setDownloadProgress(Math.ceil(percent))
  }

  const onFinishCallback = () => {
    setIsDownloading(false)
    setDownloadProgress(0)
  }

  const onDownloadAllClick = useCallback(() => {
    setIsDownloading(true)
    const filename = `AYTM-Survey-${window.surveyId}-Video-Response.zip`
    fetchData({
      customParams: {
        pageSize: totalVideoResponses,
        currentPage: 1
      },
      customFetchDataCallback: ({ cs }) => {
        const files = cs.reduce((acc, response) => {
          Object.keys(response.answer).forEach(k => {
            if (response.answer[k].v && response.answer[k].v.mp4File) {
              acc.push({
                filename: `AYTM-Survey-${window.surveyId}-Video-Response-${response.response.uid}_${questionOldIdToId(
                  k
                )}.mp4`,
                url: response.answer[k].v.mp4File
              })
            }
          })
          return acc
        }, [])
        downloadAndPackImages(filename, files, onEveryFileCallback, onFinishCallback)
      }
    })
  }, [totalVideoResponses, fetchData])

  const itemTemplate = option => (
    <span className={`${option.icon ? 'p-item-label ' : ''}p-lvl-${option.lvl || 0}`}>
      {option.hidden ? null : (
        <>
          {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
          <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
        </>
      )}
    </span>
  )

  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const orderByItemTemplate = option => (
    <span onClick={option.value == orderBy ? () => onSelectOrderBy(option) : null} data-key={option.key}>
      {option.hidden ? null : <span dangerouslySetInnerHTML={{ __html: option.text }}></span>}
    </span>
  )

  const translationItemTemplate = option =>
    option.hidden ? null : (
      <span
        id={`stat-${specialMode}-translation-${'*' == option.value ? 'original' : option.value}`}
        className={classNames(
          `stat-${specialMode}-translation`,
          `stat-${specialMode}-translation-${'*' == option.value ? 'original' : option.value}`,
          `-dtr-stat-${specialMode}-translation`,
          `-dtr-stat-${specialMode}-translation-${'*' == option.value ? 'original' : option.value}`,
          `p-lvl-${option.lvl || 0}`,
          { 'p-item-label': option.icon }
        )}
        data-stat-translation={'*' == option.value ? 'original' : option.value}
        onClick={
          readOnly
            ? null
            : () => {
                setTranslationMessage(null)
                setTranslationMessageType(null)

                onTranslationChange(option.value)
              }
        }
      >
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
        {option.translationsMissing ? <i className={`icon-aytmfd-skipper ${style['skipper-icon']}`} /> : null}
      </span>
    )

  const translationValueTemplate = (option, _props) =>
    option ? (
      Object.hasOwn(option, 'label') ? (
        <>
          <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
        </>
      ) : option.icon ? (
        <i className={`p-item-icon icon-aytmfd-${option.icon}`} />
      ) : null
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const pageSizeValueTemplate = option =>
    option ? (
      totalResponses < option.value ? (
        <>{totalResponses} found</>
      ) : isQAMode ? (
        <>Show {pageSize} per page</>
      ) : (
        <>
          {option.value}/{totalResponses}
        </>
      )
    ) : (
      <></>
    )

  useEffect(() => {
    const translationsMissingKeys = Object.keys(translationsMissing || {})

    if (translationsMissingKeys.includes(translation)) {
      const verbatimsByLocaleKey = Object.keys(totalVerbatimsByLocale || {}).find(
        locale => locale.split('_', 1)[0].toLowerCase() == translation
      )
      const total =
        totalVerbatims -
        (totalVerbatimsByLocale && Object.hasOwn(totalVerbatimsByLocale, 'verbatimsByLocaleKey')
          ? totalVerbatimsByLocale[verbatimsByLocaleKey].total
          : 0)
      setTranslationMessage(
        0 < total
          ? `Translating your verbatims - ${total - translationsMissing[translation]}/${total}`
          : `Translating your verbatims - ${translationsMissing[translation]}`
      )
      setTranslationMessageType('skipper')
    } else {
      setTranslationMessage(null)
      setTranslationMessageType(null)
    }
  }, [translation, totalVerbatims, totalVerbatimsByLocale, translationsMissing])

  useEffect(() => {
    if (
      isLoading ||
      isProcessing ||
      (dndDroppedTag && 'manual' == dndDroppedTag.type && !tagsList.some(tag => tag.tag == dndDroppedTag.tag))
    ) {
      setDndDroppedTag(null)
    }
  }, [tagsList, dndDroppedTag, isLoading, isProcessing])

  useEffect(() => {
    if ('codebook' == isShowDNDDropzone && dndTargetCodebook && !isQAMode) {
      dndTargetCodebook.connect(dndTargetCodebookRef.current)
    }
    if ('codebook-guided' == isShowDNDDropzone && dndTargetCodebook && !isQAMode) {
      dndTargetCodebook.connect(dndTargetCodebookRef.current)
    }

    return () => {
      if (dndTargetCodebook) dndTargetCodebook.disconnect()
    }
  }, [dndTargetCodebook, isShowDNDDropzone, isQAMode])

  useEffect(() => {
    if ('tag' == isShowDNDDropzone && dndTargetTag && !isQAMode) {
      dndTargetTag.connect(dndTargetTagRef.current)
    }

    return () => {
      if (dndTargetTag) dndTargetTag.disconnect()
    }
  }, [dndTargetTag, isShowDNDDropzone, isQAMode])

  useEventListener('codingPanelOpened', () => {
    onToggleAllTools(null, true)
  })

  useEventListener('assignMultiple', event => {
    setDndDroppedTag(event.detail || null)
  })

  useEventListener('assignMultipleCancel', () => {
    setDndDroppedTag(null)
  })

  useEventListener('dndStartCodebook', event => {
    if (!isTagsManagementDisabled && !isQAMode) {
      setIsShowDNDDropzone(event.detail.guided ? 'codebook-guided' : 'codebook')
    }
  })

  useEventListener('dndEndCodebook', () => {
    setIsShowDNDDropzone(false)
  })

  useEventListener('dndStartTag', () => {
    if (!isTagsManagementDisabled && !isQAMode) {
      setIsShowDNDDropzone('tag')
    }
  })

  useEventListener('dndEndTag', () => {
    setIsShowDNDDropzone(false)
  })

  const renderPageSize = () => (
    <Dropdown
      data-testid="stat-comments-page-size"
      className="p-dropdown-rounded p-dropdown-specials p-dropdown-left"
      value={pageSize}
      options={pageSizeOptions}
      optionValue="value"
      optionLabel="text"
      itemTemplate={itemTemplate}
      valueTemplate={pageSizeValueTemplate}
      fixedDropdownDir="bottom"
      disabled={readOnly}
      onChange={onLoadPerPage}
    />
  )

  const renderDND = () => (
    <div className={`${style['component-dnd']}`}>
      {dndDroppedTag ? (
        <div className={style['dnd-confirm']}>
          <div className={style['dnd-confirm-title']}>
            {error ? (
              <>Error</>
            ) : (
              <>
                <i
                  className={
                    'sentiment' == dndDroppedTag.type
                      ? `icon-aytmfd-sentiment-${SENTIMENTS_ICON_HASH[dndDroppedTag.tag]}`
                      : 'icon-aytmfd-tag'
                  }
                />
                <span
                  dangerouslySetInnerHTML={{
                    __html: loading ? `Assigning ${dndDroppedTag.tag}...` : dndDroppedTag.tag
                  }}
                />
              </>
            )}
          </div>
          {loading || error ? (
            <div className={style['dnd-confirm-status']}>
              {loading ? (
                <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
              ) : (
                <div className={style['dnd-confirm-status-error']}>{error}</div>
              )}
            </div>
          ) : (
            <div className={style['dnd-confirm-options']}>
              <div className={style['dnd-confirm-options-item']}>
                <span>
                  All comments
                  <br />
                  on this page:
                </span>
                <Button
                  className="p-button-sm p-button-rounded p-button-stat"
                  label={`Apply to ${commentsOnPageCount}`}
                  disabled={!commentsOnPageCount}
                  onClick={0 < commentsOnPageCount ? () => onTagApply(true) : null}
                />
              </div>
              <div className={style['dnd-confirm-options-item']}>
                <span>
                  All comments
                  <br />
                  found:
                </span>
                <Button
                  className="p-button-sm p-button-rounded p-button-stat"
                  label={`Apply to ${
                    ('sentiment' == dndDroppedTag.type && isDisabledSentimentInScope && !isOnlyDisabledSentiment) ||
                    (isVirtualOrXpertInScope && !isOnlyVirtualOrXpert)
                      ? 'all'
                      : totalCommentsCount
                  }`}
                  disabled={!totalCommentsCount}
                  onClick={0 < totalCommentsCount ? () => onTagApply(false) : null}
                />
              </div>
              <div className={`${style['dnd-confirm-options-item']} p-right`}>
                <Button className="p-button-sm p-button-rounded p-button-stat" label="Cancel" onClick={onTagCancel} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {'codebook' == isShowDNDDropzone && (
            <div className={style['dnd-dropzone']} ref={dndTargetCodebookRef}>
              <div className={style['dnd-dropzone-inner']}>
                Drag the codebook here
                <br />
                to apply codebook ...
              </div>
            </div>
          )}
          {'codebook-guided' == isShowDNDDropzone && (
            <div className={style['dnd-dropzone']} ref={dndTargetCodebookRef}>
              <div className={style['dnd-dropzone-inner']}>
                Drag the codebook here
                <br />
                to run guided autocode ...
              </div>
            </div>
          )}
          {'tag' == isShowDNDDropzone && (
            <div className={style['dnd-dropzone']} ref={dndTargetTagRef}>
              <div className={style['dnd-dropzone-inner']}>
                Drag the code here
                <br />
                to apply to multiple comments ...
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )

  const renderMode = () => (
    <>
      <div className={style['component-upper']}>
        <div className={`${style.block} p-col-12`}>
          <div className="p-fluid p-field p-col-7">
            {isVideoResponsesMode || isOnePerPage ? null : (
              <Dropdown
                data-testid="stat-comments-order"
                className="p-dropdown-rounded p-dropdown-specials"
                placeholder="Sort"
                value={orderBy}
                options={orderByOptions}
                optionValue="value"
                optionLabel="text"
                itemTemplate={orderByItemTemplate}
                valueTemplate={valueTemplate}
                fixedDropdownDir="bottom"
                disabled={readOnly}
                onChange={onSelectOrderBy}
              />
            )}
          </div>
          {isQAMode ? null : (
            <div className={`${style.toolbar} p-field p-right`}>
              {((isSurveyGuest && 'on' == window.features.oe_tags_management?.status) || !isTagsManagementDisabled) && (
                <div
                  className={classNames(style['toolbar-item'], {
                    [style['toolbar-item-active']]: isShowAllTools
                  })}
                  onClick={onToggleAllTools}
                  title={`${isShowAllTools ? 'Hide' : 'Show'} all tools`}
                >
                  <i className="icon-aytmfd-tag" />
                </div>
              )}
              <div
                className={classNames(style['toolbar-item'], {
                  [style['toolbar-item-active']]: isShowAllTraits,
                  [style['toolbar-item-disabled']]: !hasTraits
                })}
                onClick={hasTraits ? onToggleAllTraits : null}
                title={`${isShowAllTraits ? 'Hide' : 'Show'} all traits`}
              >
                <i className="icon-aytmfd-singleuser" />
              </div>
              <div className={style['toolbar-item']}>
                <i
                  data-testid="stat-comments-hotdog"
                  className="icon-aytmfd-hotdog"
                  title={`${isShowToolsDropdown ? 'Hide' : 'Show'} all tools`}
                  onClick={isShowToolsDropdown ? null : onToggleToolsDropdown}
                />
                <SurveyDropdown
                  questionsList={questionsList}
                  responseOrderRef={responseOrderRef}
                  opened={isShowToolsDropdown}
                  hasQuestionOrder={hasQuestionOrder}
                  isAdvancedFiltersDisabled={isAdvancedFiltersDisabled}
                  onNewFilter={!isEmptyScope ? onNewFilter : null}
                  onToggleQAMode={onToggleQAMode}
                  onClickOutside={onToggleToolsDropdown}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={style['component-lower']}>
        <div className={`${style.block} p-col-12`}>
          {isVideoResponsesMode ? (
            <div className="p-field">
              <Button
                className="p-button-rounded p-button-stat"
                label={isDownloading ? `Downloading ${downloadProgress}%` : 'DOWNLOAD'}
                disabled={isDownloadDisabled}
                onClick={isDownloadDisabled ? null : onDownloadAllClick}
              />
            </div>
          ) : null}
          {1 < translationsOptions.length ? (
            'available' == window.features.ai_assistants_oe_translation.status ? (
              <div className="p-field">
                <Popup
                  className={style['component-popup']}
                  width="320px"
                  trigger="hover"
                  popupOffset={14}
                  portal
                  direction="bottomleft"
                  content={() => (
                    <div className={style['component-popup-content']}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: `<b>${
                            window.features.ai_assistants_oe_translation.onPackage.text
                          }</b><br />Skipper will translate all verbatims to ${translationsOptions
                            .slice(1)
                            .map(option => `<b>${option.value.toUpperCase()}</b>`)
                            .join(', ')}.<br />All original responses will be preserved.`
                        }}
                      ></span>
                    </div>
                  )}
                >
                  <a
                    className="p-cmoponent p-button p-button-rounded p-button-stat"
                    href={window.features.ai_assistants_oe_translation.onPackage.upgradeURL}
                    rel="noreferrer"
                  >
                    <i class="icon-aytmfd-translate"></i>$$
                  </a>
                </Popup>
              </div>
            ) : 'on' != window.features.ai_assistants_oe_translation.status ? null : (
              <div className="p-field">
                <Dropdown
                  data-testid="stat-comments-translations"
                  id={`stat-${specialMode}-translation-selector`}
                  className={classNames(
                    'p-dropdown-rounded p-dropdown-blue p-dropdown-left',
                    `stat-${specialMode}-translation-selector`,
                    `-dtr-stat-${specialMode}-translation-selector`
                  )}
                  value={translation || '*'}
                  options={translationsOptions}
                  optionValue="value"
                  optionLabel="text"
                  itemTemplate={translationItemTemplate}
                  valueTemplate={translationValueTemplate}
                  fixedDropdownDir="bottom"
                  disabled={readOnly}
                  onChange={readOnly ? null : event => onTranslationChange(event.value)}
                />
              </div>
            )
          ) : null}
          {isQAMode ? (
            <div className={`${style.qa}} p-field p-right`}>
              <span className={style['qa-title']}>QA MODE</span>
              <span className={style['qa-toggle']} onClick={onToggleQAMode}>
                EXIT
              </span>
            </div>
          ) : (
            <>
              <div className="p-field">
                {10 >= totalResponses ? <span className="per-page">{totalResponses} found</span> : renderPageSize()}
              </div>
              {1 < totalPages ? (
                <div className="p-field p-right">
                  <Paginator
                    data-testid="stat-comments-paginator"
                    first={currentPage - 1}
                    rows={1}
                    totalRecords={totalPages}
                    template={{
                      layout: `PrevPageLink CurrentPageReport NextPageLink`,
                      PrevPageLink: options => (
                        <button
                          type="button"
                          className={options.className}
                          onClick={options.onClick}
                          disabled={options.disabled}
                        >
                          <i className="p-paginator-icon pi icon-aytmfd-prev" />
                        </button>
                      ),
                      NextPageLink: options => (
                        <button
                          type="button"
                          className={options.className}
                          onClick={options.onClick}
                          disabled={options.disabled}
                        >
                          <i className="p-paginator-icon pi icon-aytmfd-next" />
                        </button>
                      )
                    }}
                    currentPageReportTemplate="{currentPage}/{totalPages}"
                    onPageChange={event => onPageChange(event.page + 1)}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  )

  return (
    <>
      <div className={style['component-screen']} style={{ top: headerTop - 40 }}></div>
      <div
        className={classNames(style.component, {
          [style['component-static']]: !CSS_SUPPORTS_STICKY
        })}
        style={{ top: headerTop }}
      >
        <div className={`${style['component-questions']} p-fluid`}>
          <Dropdown
            data-testid="stat-comments-questions"
            className="p-dropdown-rounded p-dropdown-specials"
            placeholder="Select Question"
            value={selectedQuestion || DEFAULT_SELECTED_QUESTION}
            options={questionsListOptions}
            optionValue="value"
            optionLabel="text"
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            fixedDropdownDir="bottom"
            disabled={readOnly || isQAMode || isVideoResponsesMode}
            onChange={onSelectQuestion}
          />
        </div>
        <div className={`${style['component-search']} p-fluid`}>
          <SearchInput
            placeholder="Search"
            survey={survey}
            tagsList={tagsList}
            questionsList={questionsList}
            search={request}
            eventName="addCommentsSearchOption"
            isLoading={isLoading}
            isProcessing={isProcessing}
            disabled={isQAMode}
            isTagsManagementDisabled={isTagsManagementDisabled}
            isSentimentsManagementDisabled={isSentimentsManagementDisabled}
            onSearchChange={onSearchChange}
          />
        </div>
        {dndDroppedTag || isShowDNDDropzone ? renderDND() : renderMode()}
      </div>

      {isQAMode ? (
        <div
          className={classNames(style['bottom-bar'], {
            [style['bottom-bar-hidden']]: isQALoading
          })}
          style={{ top: headerTop + 106 }}
        >
          <div className={`${style.block} p-col-12`}>
            <div className="p-field">
              <i className={`${style['bottom-bar-hint-icon']} icon-aytmfd-qa-mode`} />
              <span className={style['bottom-bar-hint']}>
                (Shift+1 to approve{isOnePerPage ? ', Shift+3 to reject' : ''})
              </span>
            </div>
            <div className="p-field p-right">
              {0 < totalResponses ? renderPageSize() : null}
              <span className={style['bottom-bar-count']}>
                <b>{qaCounter}</b> done / <b>{qaTotal || 0}</b> left
              </span>
            </div>
          </div>
        </div>
      ) : null !== translationMessage && 0 < translationMessage.length ? (
        <div className={style['bottom-bar']} style={{ top: headerTop + 106 }}>
          <div className={`${style.block} p-col-12`}>
            <div className="p-field">
              {'skipper' == translationMessageType ? (
                <i className={`${style['bottom-bar-message-icon']} icon-aytmfd-skipper`} />
              ) : null}
              <span
                className={style['bottom-bar-message']}
                dangerouslySetInnerHTML={{ __html: translationMessage || 'translating' }}
              ></span>
            </div>
            <div className="p-field p-right">
              <span className={style['bottom-bar-refresh']} onClick={onRefresh}>
                <i className={classNames('icon-aytmfd-refresh-v2', { 'aytmfd-spin': isLoading })}></i>Refresh
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

SurveyHeaderComments.propTypes = {
  survey: PropTypes.object,
  readOnly: PropTypes.bool,
  specialMode: PropTypes.string,
  isHighlightSearchTerms: PropTypes.bool,
  isAdvancedFiltersDisabled: PropTypes.bool,
  isSpecialsSortingDisabled: PropTypes.bool,
  pageData: PropTypes.array,
  loadRejectedCS: PropTypes.string,
  questionsList: PropTypes.array,
  showTraitsRef: PropTypes.object,
  responseOrderRef: PropTypes.object,
  currentResponseRef: PropTypes.object,
  isVirtualOrXpertInScope: PropTypes.bool,
  isOnlyVirtualOrXpert: PropTypes.bool,
  setIsHighlightSearchTerms: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  onCreateFilter: PropTypes.func,
  onTranslationChange: PropTypes.func,
  onSelectedQuestionChange: PropTypes.func,
  onPageChange: PropTypes.func,
  fetchData: PropTypes.func,
  fetchDataDelay: PropTypes.func
}

SurveyHeaderComments.displayName = 'SurveyHeader'

export default SurveyHeaderComments

ADND().connect()
